import type {
  MintedAccountLinkResponse,
  StoredWhiteLabelConfig,
  TenantSuperadmin,
} from '@t4s/api-client/model'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { brandingQueryKey, fetchTenantBranding } from '../branding/gateway.ts'
import { ApiFailure, codeOf, fieldErrorsOf } from '../lib/api-error.ts'
import { messageFor } from '../lib/messages.ts'
import { NotFoundScreen } from '../screens/not-found.tsx'
import { useDocumentTitle } from '../shell/document-title.ts'
import { Notice } from '../shell/notice.tsx'
import { AccessCard, type SuperadminField } from './access-card.tsx'
import { BuildKeyCard } from './build-key-card.tsx'
import {
  createSuperadmin,
  fetchSuperadmins,
  fetchTenant,
  mintSuperadminLink,
  retireTenantById,
  rotateBuildKeyById,
  superadminsQueryKey,
  TENANTS_QUERY_KEY,
  tenantQueryKey,
} from './gateway.ts'
import { HandoffPanel } from './handoff.tsx'
import { MintLinkDialog } from './mint-link-dialog.tsx'
import {
  BrandingCard,
  ConfigurationCard,
  FlagsCard,
  IdentityCard,
  SummaryRow,
} from './overview-cards.tsx'
import { RetireDialog } from './retire-dialog.tsx'
import { RotateBuildKeyDialog } from './rotate-build-key-dialog.tsx'
import { TenantHeader } from './tenant-header.tsx'

/**
 * One tenant's page — docs/ux/PLATFORM-ADMIN.md §11, and §15's Danger zone.
 *
 * Everything this tier knows about one tenancy, and the four places to change it. What it
 * **deliberately does not show** is everything the tenant's own staff typed: no assignment,
 * no report, no client, no premises, no photograph (§6.1, §11.2). The platform owner
 * administers tenancies, not the work inside them, and every screen that does not exist is
 * a leak that cannot happen.
 *
 * §11.3 is the one exception, and it is narrow and named: a tenant's **superadmin
 * accounts**, so that a company which has locked itself out can be let back in. Minting a
 * reset link for one lets the platform owner take over that account — a real power this
 * screen does not dress up, and the confirmation says so in those words.
 *
 * **Build key replaces P03's own "Build identifier" and "Signing keys" screens.** There is
 * no platform-wide key list any more, and rotating a tenant's build key is the entire
 * surface for that tenant — so it lives here, as a card with its own confirmation
 * (`RotateBuildKeyDialog`), rather than as a route this page links out to.
 *
 * **On a retired tenant the whole page is read-only** (§11.4): the cards render, the state
 * reads Retired, both of §11.3's actions are *absent* rather than disabled, the build key
 * is neither shown nor rotatable (its builds are already refused, and the API answers a
 * retired tenant's rotation with `conflict`), and the Danger zone offers nothing because
 * retiring is done and there is no un-retire.
 *
 * **Where each read lives (P06, §11.4).** The tenant's own read decides whether the page
 * exists at all — a failure replaces everything below the header. Every other read belongs to
 * a card and fails inside it, with its own retry: the superadmin list (read here because the
 * summary row, the Access card and the hand-off all use it), the branding config (here
 * because the Branding card and the Build key card both show it), and the feature flags (in
 * the Flags card, which no one else needs). All of them read by **this page's tenant id**,
 * inside the `key={tenantId}` subtree below, so a move to another tenant starts every one of
 * them again.
 */

/**
 * One line per field, never the server's own prose (§20.1). A blank-after-trim value is
 * shadowed by this form's own Submit-disable rule, but the 120-/200-character caps
 * (`nickname`/`fullName`, `CreateSuperadminRequestSchema`) have no client-side mirror — the
 * inputs carry no `maxLength` — and are genuinely reachable here.
 *
 * `POST /platform/tenants/{tenantId}/superadmins`'s request body is flat — unlike
 * `POST /platform/tenants`'s nested `superadmin` object — so the two forms' field keys are
 * named the same but their server paths differ (`json.nickname` here,
 * `json.superadmin.nickname` there) — `chore-platform-field-error-mapping`.
 */
const SUPERADMIN_FIELD_FOR_PATH: Readonly<Record<string, SuperadminField>> = {
  nickname: 'nickname',
  fullName: 'fullName',
}

function copyForSuperadminField(field: SuperadminField): string {
  return field === 'nickname'
    ? 'Enter a nickname — at most 120 characters.'
    : 'Enter a full name — at most 200 characters.'
}

export type TenantPageProps = {
  readonly tenantId: string
}

/**
 * **One page per tenant id** — `fix-console-settings-form-keeps-other-tenant-values`. The router
 * renders one instance for every tenant id, so a move straight from tenant A's page to tenant B's
 * (the browser's history menu does it) changes only the `tenantId` prop. Every piece of state below
 * used to survive that: an open Retire or Rotate confirmation, now naming B and acting on **B** when
 * confirmed; an open mint confirmation holding A's superadmin, confirmed against B's id; A's newly
 * rotated build key and A's minted link, shown under B's name; and the mutations, whose options
 * follow the latest render, so a late answer to A's retirement was written into B's cache entry.
 * The key discards all of it and starts the page again as a fresh visit, as on Branding, Settings
 * and Feature flags. See `docs/DECISIONS.md`, "Console screens that act on one tenant are keyed by
 * tenant id".
 */
export function TenantPage({ tenantId }: TenantPageProps) {
  return <TenantPageContent key={tenantId} tenantId={tenantId} />
}

function TenantPageContent({ tenantId }: TenantPageProps) {
  const queryClient = useQueryClient()

  const [handoff, setHandoff] = useState<
    { readonly minted: MintedAccountLinkResponse; readonly nickname: string } | undefined
  >(undefined)
  const [notice, setNotice] = useState<
    { readonly tone: 'success' | 'failure' | 'warning'; readonly code: string } | undefined
  >(undefined)
  const [confirmingRetire, setConfirmingRetire] = useState(false)
  const [confirmingRotate, setConfirmingRotate] = useState(false)
  const [confirmingMint, setConfirmingMint] = useState<TenantSuperadmin | undefined>(undefined)
  const [rotated, setRotated] = useState<{ readonly key: string; readonly at: number } | undefined>(
    undefined,
  )
  // `fix-modals-close-on-success`: a failed confirm keeps its dialog open (and the typed slug in
  // it) and shows the failure inside. The refs make a double press in one frame send one request
  // — set in the press handler, released when the request settles.
  const [retireError, setRetireError] = useState<string | undefined>(undefined)
  const [rotateError, setRotateError] = useState<string | undefined>(undefined)
  const [mintError, setMintError] = useState<string | undefined>(undefined)
  const retireInFlight = useRef(false)
  const rotateInFlight = useRef(false)
  const mintInFlight = useRef(false)
  const [createFieldErrors, setCreateFieldErrors] = useState<
    Partial<Record<SuperadminField, string>>
  >({})

  const tenant = useQuery({
    queryKey: tenantQueryKey(tenantId),
    queryFn: () => fetchTenant(tenantId),
  })
  const superadmins = useQuery({
    queryKey: superadminsQueryKey(tenantId),
    queryFn: () => fetchSuperadmins(tenantId),
  })
  const branding = useQuery({
    queryKey: brandingQueryKey(tenantId),
    queryFn: () => fetchTenantBranding(tenantId),
  })

  useDocumentTitle(tenant.data?.name, 'Tenants')

  const retire = useMutation({
    mutationFn: () => retireTenantById(tenantId),
    onSuccess: (result) => {
      setConfirmingRetire(false)
      setNotice({ tone: 'warning', code: `retired:${result.sessionsRevoked}` })
      queryClient.setQueryData(tenantQueryKey(tenantId), result.tenant)
      void queryClient.invalidateQueries({ queryKey: TENANTS_QUERY_KEY })
    },
    onError: (error) => {
      setRetireError(noticeText(codeOf(error)))
    },
    onSettled: () => {
      retireInFlight.current = false
    },
  })

  const rotate = useMutation({
    // A branding read already on its way was started before this rotation, and could land after
    // it carrying the key that has just stopped working — under a heading that says "this is the
    // new key". Cancelled first, as Settings cancels its own read before a save.
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: brandingQueryKey(tenantId) })
    },
    mutationFn: () => rotateBuildKeyById(tenantId),
    onSuccess: async (result) => {
      // And again now: a read begun while the rotation was in flight may have been answered by the
      // server on either side of it, so none is allowed to land over what the rotation said.
      await queryClient.cancelQueries({ queryKey: brandingQueryKey(tenantId) })
      // The branding document carries the key; make the cached copy say what the API just
      // answered, so the Branding screen's download and this card agree without a second read.
      queryClient.setQueryData<StoredWhiteLabelConfig>(brandingQueryKey(tenantId), (old) =>
        old === undefined ? old : { ...old, buildKey: result.buildKey },
      )
      // Stamped after the write above, so the document is not "newer" than the rotation it
      // just recorded; only a later read can be.
      setRotated({ key: result.buildKey, at: Date.now() })
      setConfirmingRotate(false)
    },
    onError: (error) => {
      setRotateError(noticeText(codeOf(error)))
    },
    onSettled: () => {
      rotateInFlight.current = false
      // **This invalidation is the primary guard against a stale read landing over the new key;
      // the two `cancelQueries` calls above are a belt.** `invalidateQueries` refetches with
      // `cancelRefetch`, which supersedes any read still in flight while the query has an
      // observer, so a read begun before the rotation — or while it was in flight — can never
      // answer into the cache after this runs, whatever the cancels did. That is also why no test can tell the cancels apart (P06 review
      // N9): a held read is superseded by the invalidation however it was begun. They are kept
      // because they stop a stale answer landing in the window *before* this runs, and cost nothing.
      //
      // The cancels above can leave a **first** load of the branding document with nothing to run
      // it again — pending for ever, a skeleton with no Try again. So whatever the outcome, the
      // document is read again. After a success the server answers the new key, so a later read
      // cannot bring the old one back; after a refusal it answers the key unchanged. Where data
      // already exists this is a harmless re-read that is simply fresher than the rotation.
      void queryClient.invalidateQueries({ queryKey: brandingQueryKey(tenantId) })
    },
  })

  /**
   * Everything on this page that is derived from the tenant's accounts, read again: the
   * superadmin list **and the tenant itself** (P06 per-task QA, D2). The summary row's Accounts,
   * Signed in now and Can anyone sign in? figures come from the tenant read, not the list, so
   * refreshing only the list left Accounts at "1" under a Superadmins figure of "2" until the
   * next visit. Both names are this page's tenant id, and `exact` keeps the tenant refresh from
   * also re-reading the branding document and flags, whose keys sit under it. Run while the
   * hand-off is on screen, so "I have delivered this" returns to figures that agree. A refresh
   * that fails leaves the page as it was (the tenant read's own rule, above).
   */
  const refreshAccounts = () => {
    void queryClient.invalidateQueries({ queryKey: tenantQueryKey(tenantId), exact: true })
    void queryClient.invalidateQueries({ queryKey: superadminsQueryKey(tenantId) })
  }

  const mint = useMutation({
    mutationFn: (input: { userId: string; nickname: string }) =>
      mintSuperadminLink(tenantId, input.userId),
    onSuccess: (minted, input) => {
      setConfirmingMint(undefined)
      setHandoff({ minted, nickname: input.nickname })
      refreshAccounts()
    },
    onError: (error) => {
      // Inside the dialog, which stays open (`fix-modals-close-on-success`, now for mint too).
      setMintError(noticeText(codeOf(error)))
    },
    onSettled: () => {
      mintInFlight.current = false
    },
  })

  const create = useMutation({
    mutationFn: (input: { nickname: string; fullName: string }) =>
      createSuperadmin(tenantId, input),
    onSuccess: (minted, input) => {
      setHandoff({ minted, nickname: input.nickname })
      setCreateFieldErrors({})
      refreshAccounts()
      // The list's Users column counts the new account too.
      void queryClient.invalidateQueries({ queryKey: TENANTS_QUERY_KEY })
    },
    onError: (error) => {
      const failure = error instanceof ApiFailure ? error : undefined

      // `chore-platform-field-error-mapping`: a `validation_failed` naming a field this
      // form recognises marks that field and shows no generic notice, matching
      // `create-tenant.tsx`'s and `tenant-settings.tsx`'s identical rule. The form itself
      // stays open either way (see `AccessCard`) — nothing typed is lost on any failure,
      // field-mapped or not.
      if (failure?.code === 'validation_failed') {
        const mapped = fieldErrorsOf<SuperadminField>(
          failure,
          SUPERADMIN_FIELD_FOR_PATH,
          copyForSuperadminField,
        )
        if (Object.keys(mapped).length > 0) {
          setCreateFieldErrors(mapped)
          return
        }
      }

      setCreateFieldErrors({})
      setNotice({ tone: 'failure', code: codeOf(error) })
    },
  })

  // Only a read that has produced **nothing** replaces the page. A refresh that fails — the
  // Retire dialog asks again when it opens, §15.3 — leaves the older tenant on screen (§18.1:
  // "what is on screen stays on screen"), and the dialog says for itself that it could not read.
  if (tenant.isError && tenant.data === undefined) {
    const code = codeOf(tenant.error)
    if (code === 'not_found' || code === 'validation_failed') {
      // The same page for a mistyped URL and for a real id that no longer resolves:
      // distinguishing them tells a visitor which ids exist (§18.3) — and it is the *same
      // component* the router's own not-found renders (`NotFoundScreen`), so the sentence a
      // stranger reads does not depend on which kind of wrong address they typed. A
      // malformed `tenantId` — one that fails the API's id-shape check before it ever looks the
      // tenant up — comes back as `validation_failed` rather than `not_found`, but it is the
      // same route-parameter read, not a form: this screen submits nothing that could
      // produce `validation_failed` on this query, so folding it into Not found here
      // cannot swallow a genuine validation failure (§18.3 requires the two to read
      // identically; F-1).
      return <NotFoundScreen />
    }
    return (
      <main className="page">
        <TenantHeader tenantId={tenantId} />
        <Notice tone="failure">{messageFor('tenant', code)}</Notice>
        <div>
          <button
            type="button"
            className="button"
            onClick={() => {
              void tenant.refetch()
            }}
          >
            Try again
          </button>
        </div>
      </main>
    )
  }

  const row = tenant.data
  const retired = row?.state === 'retired'

  if (handoff !== undefined && row !== undefined) {
    return (
      <main className="page">
        <h1>{row.name}</h1>
        <HandoffPanel
          nickname={handoff.minted.superadmin.nickname ?? handoff.nickname}
          fullName={
            superadmins.data?.find((user) => user.userId === handoff.minted.superadmin.userId)
              ?.fullName ?? handoff.minted.superadmin.fullName
          }
          link={handoff.minted.link}
          slug={row.slug}
          onDone={() => {
            setHandoff(undefined)
          }}
        />
      </main>
    )
  }

  const retryBranding = () => {
    void branding.refetch()
  }
  const retrySuperadmins = () => {
    void superadmins.refetch()
  }

  return (
    <main className="page">
      <TenantHeader tenantId={tenantId} />

      {notice === undefined ? (
        retired ? (
          <Notice tone="warning" tag="Retired">
            This tenant is retired. Everything here is read-only; its slug stays reserved forever
            and nothing was deleted.
          </Notice>
        ) : null
      ) : (
        <Notice tone={notice.tone} assertive={notice.tone !== 'success'}>
          {noticeText(notice.code)}
        </Notice>
      )}

      <SummaryRow
        tenant={row}
        superadmins={{
          data: superadmins.data,
          isError: superadmins.isError,
        }}
      />

      <div className="cards">
        <IdentityCard tenant={row} />
        <ConfigurationCard tenant={row} tenantId={tenantId} />
        <BrandingCard
          tenantId={tenantId}
          branding={{
            data: branding.data,
            isPending: branding.isPending,
            isError: branding.isError,
            error: branding.error,
            dataUpdatedAt: branding.dataUpdatedAt,
            refetch: retryBranding,
          }}
        />
        <FlagsCard tenantId={tenantId} />
        <BuildKeyCard
          tenant={row}
          branding={{
            data: branding.data,
            isPending: branding.isPending,
            isError: branding.isError,
            error: branding.error,
            dataUpdatedAt: branding.dataUpdatedAt,
            refetch: retryBranding,
          }}
          rotated={rotated}
          onRotate={() => {
            setRotateError(undefined)
            setConfirmingRotate(true)
          }}
        />
        <AccessCard
          tenant={row}
          superadmins={{
            data: superadmins.data,
            isPending: superadmins.isPending,
            isError: superadmins.isError,
            error: superadmins.error,
            refetch: retrySuperadmins,
          }}
          minting={mint.isPending}
          onMint={(user) => {
            setMintError(undefined)
            setConfirmingMint(user)
          }}
          onCreate={(input) => {
            create.mutate(input)
          }}
          creating={create.isPending}
          createFieldErrors={createFieldErrors}
          onDiscardCreateFieldErrors={() => {
            setCreateFieldErrors({})
          }}
        />

        {row === undefined || retired ? null : (
          <section className="danger-zone span-2" aria-labelledby="danger">
            <div className="card-head">
              <h2 id="danger">Danger zone</h2>
            </div>
            <div className="danger-row">
              <div className="stack-tight">
                <strong>Retire this tenant</strong>
                <span className="small secondary">
                  Its builds stop working, everybody signed in is signed out, and its slug stays
                  reserved forever. There is no un-retire.
                </span>
              </div>
              <button
                type="button"
                className="button button-danger"
                onClick={() => {
                  setRetireError(undefined)
                  setConfirmingRetire(true)
                }}
              >
                Retire this tenant…
              </button>
            </div>
          </section>
        )}
      </div>

      {confirmingRetire && row !== undefined ? (
        <RetireDialog
          tenant={row}
          pending={retire.isPending}
          error={retireError}
          onCancel={() => {
            setConfirmingRetire(false)
            setRetireError(undefined)
          }}
          onConfirm={() => {
            if (retireInFlight.current) {
              return
            }
            retireInFlight.current = true
            setRetireError(undefined)
            retire.mutate()
          }}
        />
      ) : null}

      {confirmingRotate && row !== undefined ? (
        <RotateBuildKeyDialog
          tenant={row}
          pending={rotate.isPending}
          error={rotateError}
          onCancel={() => {
            setConfirmingRotate(false)
            setRotateError(undefined)
          }}
          onConfirm={() => {
            if (rotateInFlight.current) {
              return
            }
            rotateInFlight.current = true
            setRotateError(undefined)
            rotate.mutate()
          }}
        />
      ) : null}

      {confirmingMint === undefined ? null : (
        <MintLinkDialog
          user={confirmingMint}
          pending={mint.isPending}
          error={mintError}
          onCancel={() => {
            setConfirmingMint(undefined)
            setMintError(undefined)
          }}
          onConfirm={() => {
            if (mintInFlight.current) {
              return
            }
            mintInFlight.current = true
            setMintError(undefined)
            mint.mutate({
              userId: confirmingMint.userId,
              nickname: confirmingMint.nickname ?? confirmingMint.fullName,
            })
          }}
        />
      )}
    </main>
  )
}

/**
 * The one notice this screen writes itself rather than resolving from a code.
 *
 * `retired:<n>` is a result rather than a failure, and it states the number the
 * confirmation promised — how many people that retirement actually signed out. It no longer
 * mentions a hostname teardown: there is none (§15.1, amended 2026-08-23).
 */
function noticeText(code: string): string {
  if (code.startsWith('retired:')) {
    const count = Number(code.slice('retired:'.length))
    return `This tenant is retired. ${count} ${count === 1 ? 'sign-in was' : 'sign-ins were'} ended. Its slug stays reserved.`
  }
  return messageFor('tenant', code)
}
